import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Row, Col, Typography, Tag, Tooltip } from 'antd';
import {
  LaptopOutlined,
  DesktopOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  CloudServerOutlined,
  LinkOutlined,
  HourglassOutlined,
  SyncOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import { DataCard, StatCard } from '../common';
import { useTenant } from '../../context/TenantContext';
import { summarizeLifecycleWarranties } from '../../utils/scalepadAssets';
import {
  useScalePadIdFromCipp,
  useScalePadClientSummary,
  useScalePadWarranty,
  useScalePadHardwareAssets,
} from '../../hooks';

const { Text } = Typography;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const MetricLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetricValue = styled(Text)`
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

const HealthIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HealthDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $status, theme }) => {
    switch ($status) {
      case 'healthy': return theme.colors.success;
      case 'warning': return theme.colors.warning;
      case 'critical': return theme.colors.error;
      default: return theme.colors.textMuted;
    }
  }};
`;

const MappingWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${({ theme }) => theme.spacing.sm};
  background: var(--odin-status-awaiting-bg);
  border: 1px solid var(--odin-status-awaiting-border);
  border-radius: 4px;
  color: var(--odin-status-awaiting-text);
  font-size: 12px;
  margin-bottom: 12px;
`;

const AssetTypeBreakdown = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

/**
 * AssetsWidget - Displays ScalePad hardware asset data
 *
 * This component integrates with ScalePad-MCP to display:
 * - Total device counts and types
 * - Warranty status (expiring, expired)
 * - End of Life (EOL) devices requiring attention
 * - Asset type breakdown (desktops, laptops, servers)
 *
 * Uses customerMappingService (via useScalePadIdFromCipp) to resolve
 * CIPP tenant IDs to ScalePad client/site IDs.
 *
 * @param {Object} props
 * @param {string} props.clientId - Optional direct ScalePad client ID (overrides mapping)
 * @param {Object} props.data - Legacy data prop for backward compatibility
 * @param {boolean} props.loading - Loading state (legacy)
 * @param {Object} props.error - Error object (legacy)
 * @param {Function} props.onRefresh - Refresh callback
 * @param {boolean} props.compact - Show compact StatCard view
 */
const AssetsWidget = ({
  clientId: propClientId = null,
  data: legacyData = null,
  loading: legacyLoading = false,
  error: legacyError = null,
  onRefresh,
  compact = false,
}) => {
  // Get selected tenant from context (CIPP tenant ID)
  const { selectedTenant } = useTenant();
  const cippTenantId = selectedTenant?.id;

  // Resolve CIPP tenant ID to ScalePad client ID using customer mapping service
  const {
    scalepadId: mappedScalePadId,
    isResolved,
    isLoading: isMappingLoading,
    customer,
  } = useScalePadIdFromCipp(cippTenantId, {
    enabled: !!cippTenantId && !propClientId,
  });

  // Use prop clientId if provided, otherwise use mapped ScalePad ID
  const scalepadId = propClientId || mappedScalePadId;
  const hasScalePadMapping = !!propClientId || isResolved;

  // Fetch ScalePad hardware summary data
  const {
    data: summaryData,
    isLoading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary,
  } = useScalePadClientSummary(scalepadId, {
    enabled: !!scalepadId,
  });

  // Fetch ScalePad warranty/lifecycle data
  const {
    data: lifecycleData,
    isLoading: lifecycleLoading,
    error: lifecycleError,
    refetch: refetchLifecycle,
  } = useScalePadWarranty(scalepadId, {
    enabled: !!scalepadId,
  });

  // Fetch ScalePad hardware assets with full details (for lifecycle metrics)
  const {
    data: assetsData,
    isLoading: assetsLoading,
    error: assetsError,
    refetch: refetchAssets,
  } = useScalePadHardwareAssets(scalepadId, {}, {
    enabled: !!scalepadId,
  });

  // Calculate metrics from ScalePad data
  const metrics = useMemo(() => {
    // If legacy data is provided, use it for backward compatibility
    if (legacyData && Object.keys(legacyData).length > 0) {
      return {
        totalAssets: legacyData.totalAssets ?? null,
        warrantyExpiring: legacyData.warrantyExpiring ?? null,
        warrantyExpired: legacyData.endOfLife ?? null,
        underContract: legacyData.underContract ?? null,
        desktops: legacyData.desktops ?? null,
        laptops: legacyData.laptops ?? null,
        servers: legacyData.servers ?? null,
        healthStatus: null,
      };
    }

    // Parse summary from ScalePad-MCP response
    let parsedSummary = null;
    if (summaryData) {
      if (typeof summaryData === 'string') {
        try {
          parsedSummary = JSON.parse(summaryData);
        } catch {
          parsedSummary = null;
        }
      } else if (summaryData.data) {
        parsedSummary = summaryData;
      } else if (summaryData.content?.[0]?.text) {
        try {
          parsedSummary = JSON.parse(summaryData.content[0].text);
        } catch {
          parsedSummary = null;
        }
      } else {
        parsedSummary = summaryData;
      }
    }

    // Parse lifecycle data from ScalePad-MCP response
    let parsedLifecycle = null;
    if (lifecycleData) {
      if (typeof lifecycleData === 'string') {
        try {
          parsedLifecycle = JSON.parse(lifecycleData);
        } catch {
          parsedLifecycle = null;
        }
      } else if (lifecycleData.data) {
        parsedLifecycle = lifecycleData;
      } else if (lifecycleData.content?.[0]?.text) {
        try {
          parsedLifecycle = JSON.parse(lifecycleData.content[0].text);
        } catch {
          parsedLifecycle = null;
        }
      } else {
        parsedLifecycle = lifecycleData;
      }
    }

    // Extract counts from summary response
    // Note: API returns { summary: { total_assets, by_type, ... } }
    const summaryInfo = parsedSummary?.summary || parsedSummary?.data || parsedSummary || {};
    const totalAssets = summaryInfo.total_assets || summaryInfo.totalDevices || summaryInfo.total || summaryInfo.num_hardware_assets || null;

    // Extract type breakdown (API may return uppercase keys like WORKSTATION, NETWORK, VIRTUAL)
    const byType = summaryInfo.by_type || summaryInfo.byType || summaryInfo.breakdown || {};
    const desktops = byType.Desktop || byType.desktop || byType.DESKTOP || null;
    const laptops = byType.Laptop || byType.laptop || byType.LAPTOP || null;
    const servers = byType.Server || byType.server || byType.SERVER || byType.VIRTUAL || null;
    const workstations = byType.Workstation || byType.workstation || byType.WORKSTATION || null;
    const networkDevices = byType.Network || byType.network || byType.NETWORK || null;

    // Extract lifecycle metrics from lifecycle data
    const lifecycleInfo = parsedLifecycle?.data || parsedLifecycle || {};
    // `query_hardware_lifecycle` emits `lifecycles`; the older keys are kept for legacy payloads.
    const devices = lifecycleInfo.lifecycles || lifecycleInfo.devices || lifecycleInfo.assets || [];

    // Warranty counts come from each row's `warranty.end_date` (utils/scalepadAssets.js) —
    // the rows never carried the flat `warrantyStatus` this used to read.
    let { warrantyExpiring, warrantyExpired, underContract } = summarizeLifecycleWarranties(devices);
    let eolDevices = 0;

    if (Array.isArray(devices)) {
      devices.forEach(device => {
        const lifecycleStatus = device.lifecycleStatus || device.lifecycle_status || '';

        // Count end-of-life devices
        if (lifecycleStatus.toLowerCase() === 'eol' ||
            lifecycleStatus.toLowerCase() === 'end of life' ||
            lifecycleStatus.toLowerCase() === 'retired') {
          eolDevices++;
        }
      });
    }

    // Also check for aggregated counts in the response
    const summaryMetrics = lifecycleInfo.summary || lifecycleInfo.metrics || {};
    warrantyExpiring = warrantyExpiring || summaryMetrics.warrantyExpiring || summaryMetrics.expiring_soon || 0;
    warrantyExpired = warrantyExpired || summaryMetrics.warrantyExpired || summaryMetrics.expired || 0;
    eolDevices = eolDevices || summaryMetrics.endOfLife || summaryMetrics.eol_count || 0;

    // Determine health status based on EOL and warranty expired counts
    let healthStatus = 'healthy';
    const total = totalAssets || devices.length || 0;
    if (total > 0) {
      const criticalRatio = (warrantyExpired + eolDevices) / total;
      const warningRatio = warrantyExpiring / total;

      if (criticalRatio > 0.2) {
        healthStatus = 'critical';
      } else if (criticalRatio > 0.1 || warningRatio > 0.2) {
        healthStatus = 'warning';
      }
    }

    return {
      totalAssets: totalAssets || (devices.length > 0 ? devices.length : null),
      warrantyExpiring: warrantyExpiring || null,
      warrantyExpired: warrantyExpired || null,
      endOfLife: eolDevices || null,
      underContract: underContract || null,
      desktops,
      laptops,
      servers,
      workstations,
      networkDevices,
      healthStatus,
    };
  }, [legacyData, summaryData, lifecycleData]);

  // Calculate contract rate
  const contractRate = metrics.totalAssets && metrics.underContract
    ? Math.round((metrics.underContract / metrics.totalAssets) * 100)
    : null;

  // Calculate lifecycle-focused metrics from detailed assets data
  const lifecycleMetrics = useMemo(() => {
    // Parse assets from MCP response
    let parsedAssets = [];
    if (assetsData) {
      if (typeof assetsData === 'string') {
        try {
          const parsed = JSON.parse(assetsData);
          parsedAssets = parsed.assets || parsed.data?.assets || [];
        } catch {
          parsedAssets = [];
        }
      } else if (assetsData.content?.[0]?.text) {
        try {
          const parsed = JSON.parse(assetsData.content[0].text);
          parsedAssets = parsed.assets || parsed.data?.assets || [];
        } catch {
          parsedAssets = [];
        }
      } else if (assetsData.assets) {
        parsedAssets = assetsData.assets;
      } else if (assetsData.data?.assets) {
        parsedAssets = assetsData.data.assets;
      }
    }

    if (!parsedAssets.length) {
      return {
        avgDeviceAge: null,
        refreshNeeded: null,
        eolNext90Days: null,
        storageAlerts: null,
      };
    }

    // 1. Calculate Average Device Age
    const devicesWithAge = parsedAssets.filter(a => a.age?.age_years != null);
    const avgDeviceAge = devicesWithAge.length > 0
      ? (devicesWithAge.reduce((sum, a) => sum + a.age.age_years, 0) / devicesWithAge.length).toFixed(1)
      : null;

    // 2. Count devices >5 years old (Refresh Needed)
    const refreshNeeded = parsedAssets.filter(a =>
      a.age?.age_years != null && a.age.age_years >= 5
    ).length;

    // 3. Count devices with EOL in next 90 days
    const now = new Date();
    const ninetyDaysFromNow = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
    const eolNext90Days = parsedAssets.filter(a => {
      if (!a.lifecycle?.eol_date) return false;
      const eolDate = new Date(a.lifecycle.eol_date);
      return eolDate > now && eolDate <= ninetyDaysFromNow;
    }).length;

    // 4. Count devices with >80% disk utilization
    const storageAlerts = parsedAssets.filter(a => {
      const disks = a.configuration?.disks || [];
      if (disks.length === 0) return false;
      return disks.some(disk => {
        const total = parseFloat(disk.total_gb) || parseFloat(disk.total_bytes) / (1024 * 1024 * 1024);
        const used = parseFloat(disk.used_gb) || parseFloat(disk.used_bytes) / (1024 * 1024 * 1024);
        if (!total || !used) return false;
        return (used / total) >= 0.8;
      });
    }).length;

    return {
      avgDeviceAge,
      refreshNeeded,
      eolNext90Days,
      storageAlerts,
    };
  }, [assetsData]);

  // Determine loading state
  const isLoading = legacyLoading || isMappingLoading || summaryLoading || lifecycleLoading || assetsLoading;

  // Determine error state
  const error = legacyError || summaryError || lifecycleError || assetsError;

  // Refresh handler
  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh();
    }
    if (scalepadId) {
      refetchSummary();
      refetchLifecycle();
      refetchAssets();
    }
  }, [onRefresh, scalepadId, refetchSummary, refetchLifecycle, refetchAssets]);

  // Show mapping status warning if no ScalePad mapping found
  const showMappingWarning = cippTenantId && !hasScalePadMapping && !isMappingLoading && !propClientId;

  // Compact view (StatCard)
  if (compact) {
    return (
      <StatCard
        icon={LaptopOutlined}
        value={metrics.totalAssets}
        label="Managed Assets"
        loading={isLoading}
        tooltip={
          hasScalePadMapping
            ? `Assets tracked in ScalePad${customer ? ` (${customer.displayName})` : ''}`
            : 'Configure ScalePad mapping in Odin-MCP to view data'
        }
      />
    );
  }

  // Full widget view
  return (
    <DataCard
      title="ScalePad"
      icon={LaptopOutlined}
      loading={isLoading}
      error={error}
      onRefresh={handleRefresh}
      empty={!metrics.totalAssets && !showMappingWarning}
      extra={
        metrics.healthStatus && metrics.totalAssets && (
          <Tooltip title={`Asset health: ${metrics.healthStatus}`}>
            <HealthIndicator>
              <HealthDot $status={metrics.healthStatus} />
              <Text style={{ fontSize: 12, color: 'var(--odin-text-secondary)' }}>
                {metrics.healthStatus === 'healthy' ? 'Healthy' :
                 metrics.healthStatus === 'warning' ? 'Warning' : 'Critical'}
              </Text>
            </HealthIndicator>
          </Tooltip>
        )
      }
    >
      {showMappingWarning && (
        <MappingWarning>
          <LinkOutlined />
          <span>
            No ScalePad mapping found for this tenant. Add mapping in Odin-MCP config.
          </span>
        </MappingWarning>
      )}

      <Row gutter={[16, 16]}>
        {/* Row 1: Total Assets & Avg Device Age */}
        <Col span={12}>
          <MetricRow>
            <MetricLabel>
              <DesktopOutlined />
              Total Assets
            </MetricLabel>
            <MetricValue>{metrics.totalAssets ?? '--'}</MetricValue>
          </MetricRow>
        </Col>
        <Col span={12}>
          <MetricRow>
            <MetricLabel>
              <HourglassOutlined />
              Avg Device Age
            </MetricLabel>
            <MetricValue>
              {lifecycleMetrics.avgDeviceAge ? `${lifecycleMetrics.avgDeviceAge} yrs` : '--'}
            </MetricValue>
          </MetricRow>
        </Col>

        {/* Row 2: Refresh Needed & EOL Next 90d */}
        <Col span={12}>
          <MetricRow>
            <MetricLabel>
              <SyncOutlined style={{ color: lifecycleMetrics.refreshNeeded > 0 ? 'var(--odin-warning)' : undefined }} />
              Refresh Needed
            </MetricLabel>
            <MetricValue>
              {lifecycleMetrics.refreshNeeded ?? '--'}
              {lifecycleMetrics.refreshNeeded > 0 && (
                <Tag color="orange" style={{ marginLeft: 8 }}>&gt;5 years</Tag>
              )}
            </MetricValue>
          </MetricRow>
        </Col>
        <Col span={12}>
          <MetricRow>
            <MetricLabel>
              <ClockCircleOutlined style={{ color: lifecycleMetrics.eolNext90Days > 0 ? 'var(--odin-error)' : undefined }} />
              EOL Next 90d
            </MetricLabel>
            <MetricValue>
              {lifecycleMetrics.eolNext90Days ?? '--'}
              {lifecycleMetrics.eolNext90Days > 0 && (
                <Tag color="red" style={{ marginLeft: 8 }}>Action</Tag>
              )}
            </MetricValue>
          </MetricRow>
        </Col>

        {/* Row 3: Storage Alerts */}
        <Col span={12}>
          <MetricRow>
            <MetricLabel>
              <DatabaseOutlined style={{ color: lifecycleMetrics.storageAlerts > 0 ? 'var(--odin-warning)' : undefined }} />
              Storage Alerts
            </MetricLabel>
            <MetricValue>
              {lifecycleMetrics.storageAlerts ?? '--'}
              {lifecycleMetrics.storageAlerts > 0 && (
                <Tag color="orange" style={{ marginLeft: 8 }}>&gt;80%</Tag>
              )}
            </MetricValue>
          </MetricRow>
        </Col>
        {(metrics.desktops !== null || metrics.laptops !== null || metrics.servers !== null || metrics.workstations !== null || metrics.networkDevices !== null) && (
          <Col span={24}>
            <MetricLabel style={{ marginBottom: 8 }}>Asset Types</MetricLabel>
            <AssetTypeBreakdown>
              {metrics.workstations !== null && metrics.workstations > 0 && (
                <Tag icon={<DesktopOutlined />}>Workstations: {metrics.workstations}</Tag>
              )}
              {metrics.desktops !== null && metrics.desktops > 0 && (
                <Tag icon={<DesktopOutlined />}>Desktops: {metrics.desktops}</Tag>
              )}
              {metrics.laptops !== null && metrics.laptops > 0 && (
                <Tag icon={<LaptopOutlined />}>Laptops: {metrics.laptops}</Tag>
              )}
              {metrics.servers !== null && metrics.servers > 0 && (
                <Tag icon={<CloudServerOutlined />}>Servers: {metrics.servers}</Tag>
              )}
              {metrics.networkDevices !== null && metrics.networkDevices > 0 && (
                <Tag icon={<CloudServerOutlined />}>Network: {metrics.networkDevices}</Tag>
              )}
            </AssetTypeBreakdown>
          </Col>
        )}
      </Row>
    </DataCard>
  );
};

AssetsWidget.propTypes = {
  /** Direct ScalePad client ID (bypasses customer mapping) */
  clientId: PropTypes.string,
  /** Legacy data prop for backward compatibility */
  data: PropTypes.shape({
    totalAssets: PropTypes.number,
    warrantyExpiring: PropTypes.number,
    endOfLife: PropTypes.number,
    underContract: PropTypes.number,
    desktops: PropTypes.number,
    laptops: PropTypes.number,
    servers: PropTypes.number,
  }),
  /** Legacy loading state */
  loading: PropTypes.bool,
  /** Legacy error object */
  error: PropTypes.object,
  /** Callback when refresh is requested */
  onRefresh: PropTypes.func,
  /** Show compact StatCard view */
  compact: PropTypes.bool,
};

export default AssetsWidget;
